import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, effect, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DropzoneVariant } from '../dropzone/dropzone-variant.interface';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { distinctUntilChanged } from 'rxjs';
import { EinvoiceDocumentComponent } from '../einvoice-document/einvoice-document.component';
import { EinvoicePdfViewComponent } from '../pdf-view/pdf-view.component';
import { TechnicalDetailsDialogComponent } from '../../shared/modals/technical-details-dialog/technical-details-dialog.component';
import { Attachment, EInvoiceValidationState, EInvoiceViewerService } from '../../services/einvoice-viewer.service';
import { EInvoiceValidationMode, EInvoiceViewerConfiguration } from '../../interfaces/einvoice-viewer-configuration.interface';
import { ActivatedRoute } from '@angular/router';
import { MatIconRegistry } from '@angular/material/icon';
import { FormatMatchResult } from '../../interfaces/format-match-result.interface';
import { EInvoiceViewerSearchOptions } from '../../interfaces/einvoice-viewer-search-options.interface';
import { EInvoiceViewerView } from '../../interfaces/einvoice-viewer-view.type';
import { EinvoiceViewerContextService } from '../../services/einvoice-viewer-context.service';
import { EinvoiceViewerSearchService } from '../../services/einvoice-viewer-search.service';
import { EInvoiceFieldSelection } from '../../utils/provenance-index';
import { EinvoicePrintService } from '../../services/einvoice-print.service';

declare const $localize: (messageParts: TemplateStringsArray, ...expressions: any[]) => string;

@Component({
    selector: 'app-einvoice-viewer',
    templateUrl: './einvoice-viewer.component.html',
    styleUrls: ['./einvoice-viewer.component.scss'],
    providers: [EinvoiceViewerContextService, EinvoiceViewerSearchService, EinvoicePrintService],
  changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EinvoiceViewerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() document: any | undefined;
  @Input() format: FormatMatchResult | undefined;
  @Input() configuration: EInvoiceViewerConfiguration | undefined;
  @Input() additionalAttachments: Attachment[] | undefined;
  @Input() rawXml: string | undefined;
  @Input() xmlFileName: string | undefined;
  @Input() pdfBytes: Uint8Array | undefined;
  @Input() pdfFileName = 'document.pdf';
  @Input() selectedModelPaths: string[] | undefined;
  @Input() focusedModelPath: string | undefined;
  @Output() fileAdded = new EventEmitter<string>();
  @Output() onError = new EventEmitter<{ message: string, title?: string }>();
  @Output() selectionChanged = new EventEmitter<EInvoiceFieldSelection[]>();
  @Output() validationRequested = new EventEmitter<void>();
  @ViewChild('viewerToolbar')
  set viewerToolbarRef(toolbar: ElementRef<HTMLElement> | undefined) {
    if (this.viewerToolbar?.nativeElement === toolbar?.nativeElement) {
      return;
    }

    this.toolbarResizeObserver?.disconnect();
    this.viewerToolbar = toolbar;
    this.lastObservedToolbarWidth = undefined;

    const element = toolbar?.nativeElement;
    if (element && typeof ResizeObserver !== 'undefined') {
      this.toolbarResizeObserver = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width === undefined || width === this.lastObservedToolbarWidth) {
          return;
        }
        this.lastObservedToolbarWidth = width;
        this.scheduleToolbarLayout();
      });
      this.toolbarResizeObserver.observe(element.closest('.main-wrapper') ?? element);
    }

    this.scheduleToolbarLayout();
  }
  @ViewChild('einvoice', { read: EinvoiceDocumentComponent }) einvoice: EinvoiceDocumentComponent | undefined;
  @ViewChild('einvoice', { read: ElementRef }) private documentElement: ElementRef<HTMLElement> | undefined;
  @ViewChild(EinvoicePdfViewComponent) pdfView: EinvoicePdfViewComponent | undefined;

  zoomLevel = 0;
  readonly MIN_ZOOM_LEVEL = -10;
  readonly MAX_ZOOM_LEVEL = 20;
  readonly ZOOM_STEP = 1;

  private static readonly FORMAT_LOGOS = new Set<string>([
    'factur-x-minimum',
    'factur-x-basicwl',
    'factur-x-basic',
    'factur-x-en16931',
    'factur-x-extended'
  ]);

  processing: boolean = false;
  validationState: EInvoiceValidationState = { status: 'idle' };
  dragOver = false;
  filename: string | undefined;
  activeView: EInvoiceViewerView = 'document';
  pdfViewCreated = false;
  showButtonsInToolbar = true;
  showValidationInToolbar = true;
  showSearchInToolbar = true;
  showViewSwitcherInToolbar = true;
  printing = false;
  preparingPrint = false;
  private destroyed = false;

  private _queued: File[] = [];
  private lastEmittedSelectionSignature = '';
  private viewerToolbar: ElementRef<HTMLElement> | undefined;
  private toolbarResizeObserver: ResizeObserver | undefined;
  private toolbarLayoutFrame: number | undefined;
  private lastObservedToolbarWidth: number | undefined;

  constructor(
    private dialog: MatDialog,
    private einvoiceViewerService: EInvoiceViewerService,
    private einvoiceViewerContextService: EinvoiceViewerContextService,
    private einvoiceViewerSearchService: EinvoiceViewerSearchService,
    private activatedRoute: ActivatedRoute,
    private matIconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private einvoicePrintService: EinvoicePrintService
  ) {
    effect(() => {
      const selectedEntries = this.einvoiceViewerContextService.selectedEntries();
      const signature = selectedEntries.map(entry => entry.modelPath).join('|');
      if (signature !== this.lastEmittedSelectionSignature) {
        this.lastEmittedSelectionSignature = signature;
        this.selectionChanged.emit(selectedEntries);
      }
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['document'] || changes['format'] || changes['rawXml'] || changes['xmlFileName']) {
      this.updateContextFromInputs();
    }
    if (changes['configuration'] || changes['pdfBytes']) {
      this.ensureActiveViewAvailable();
      this.updatePdfViewLifecycle(changes['pdfBytes']?.previousValue);
    }
    if (changes['selectedModelPaths'] || changes['focusedModelPath']) {
      this.applyExternalSelection();
    }

    this.scheduleToolbarLayout();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.toolbarResizeObserver?.disconnect();
    if (this.toolbarLayoutFrame !== undefined) {
      cancelAnimationFrame(this.toolbarLayoutFrame);
    }
  }

  ngOnInit(): void {
    this.registerSvgIcons();
    this.einvoiceViewerService.dragChanged$.pipe(
      distinctUntilChanged()
    ).subscribe(($event) => {
      this.dragOver = $event;
      this.cdr.markForCheck();
    });
    this.einvoiceViewerService.document$.subscribe(result => {
      if (result === undefined) {
        this.updateContextFromInputs();
        this.cdr.markForCheck();
        return;
      }

      this.einvoiceViewerSearchService.reset();
      this.document = undefined;
      this.document = result?.model;
      this.format = result?.format;
      this.additionalAttachments = result?.additionalAttachments;
      this.rawXml = result?.rawXml;
      this.xmlFileName = result?.xmlFileName;
      const previousPdfBytes = this.pdfBytes;
      this.pdfBytes = result?.pdfBytes;
      this.ensureActiveViewAvailable();
      this.updatePdfViewLifecycle(previousPdfBytes);
      this.updateContextFromInputs();
      this.dragOver = false;
      if (this.configuration && this.configuration.defaultZoomLevel) {
        this.zoomLevel = this.configuration.defaultZoomLevel ?? 0;
      }
      this.scheduleToolbarLayout();
      this.cdr.markForCheck();
    });
    this.einvoiceViewerService.processing$.subscribe(processing =>
      {
        this.processing = processing;
        this.cdr.markForCheck();
      }
    );
    this.einvoiceViewerService.validationState$.subscribe(state => {
      this.validationState = state;
      this.scheduleToolbarLayout();
      this.cdr.markForCheck();
    });
    this.activatedRoute.data.subscribe(data => {
      if (data["configuration"]) {
        this.configuration = data["configuration"];
        if (this.configuration && this.configuration.defaultZoomLevel) {
          this.zoomLevel = this.configuration.defaultZoomLevel ?? 0;
        }
        this.scheduleToolbarLayout();
        this.cdr.markForCheck();
      }
    });
    if (this.configuration && this.configuration.defaultZoomLevel) {
      this.zoomLevel = this.configuration.defaultZoomLevel ?? 0;
    }

  }

  get dropzoneVariant(): DropzoneVariant {
    //return this.document ? 'small' : 'full';
    return "full";
  }

  filesAdded(files: File[]) {
    this.document = undefined;
    this.rawXml = undefined;
    this.xmlFileName = undefined;
    this.pdfBytes = undefined;
    this.pdfViewCreated = false;
    this.einvoiceViewerSearchService.reset();
    this.updateContextFromInputs();
    this.dragOver = false;
    this.einvoiceViewerService.filesAdded(files);
  }

  setActiveView(view: EInvoiceViewerView): void {
    if (!this.isViewEnabled(view)) {
      return;
    }

    if (view === 'pdf') {
      this.pdfViewCreated = true;
    }
    this.activeView = view;
    this.einvoiceViewerSearchService.setActiveView(view);
  }

  private updatePdfViewLifecycle(previousPdfBytes: Uint8Array | undefined): void {
    if (!this.pdfViewEnabled) {
      this.pdfViewCreated = false;
      return;
    }

    if (previousPdfBytes !== this.pdfBytes && this.activeView !== 'pdf') {
      this.pdfViewCreated = false;
    }

    if (this.activeView === 'pdf') {
      this.pdfViewCreated = true;
    }
  }

  zoomIn() {
    if (this.zoomLevel < this.MAX_ZOOM_LEVEL) {
      this.zoomLevel++;
    }
  }
  zoomOut() {
    if (this.zoomLevel > this.MIN_ZOOM_LEVEL) {
      this.zoomLevel--;
    }
  }
  get zoomLevelFactor(): number {
    return 1 + (this.zoomLevel / 10);
  }
  resetZoom() {
    this.zoomLevel = this.configuration?.defaultZoomLevel ?? 0;
  }

  uploadClicked() {
    this.einvoiceViewerService.uploadClicked$.next();
  }
  async print(): Promise<void> {
    if (this.printing) {
      return;
    }

    this.printing = true;
    try {
      if (!this.document) {
        throw new Error('No invoice is available to print.');
      }
      this.preparingPrint = true;
      this.cdr.detectChanges();
      const element = this.documentElement?.nativeElement;
      if (!element) {
        throw new Error('The invoice document could not be rendered for printing.');
      }
      const printed = this.einvoicePrintService.print(element, this.filename);
      this.preparingPrint = false;
      this.cdr.detectChanges();
      await printed;
    } catch (error) {
      if (!this.destroyed) {
        console.error('Invoice printing failed.', error);
        this.error({ message: $localize`:@@einvoice.print.error:Unable to prepare the invoice for printing. Please try again.` });
      }
    } finally {
      this.printing = false;
      this.preparingPrint = false;
      if (!this.destroyed) {
        this.cdr.markForCheck();
      }
    }
  }
  showTechnicalInformations() {
    this.dialog.open(TechnicalDetailsDialogComponent, {
      width: "70%",
      data: { model: this.document, format: this.format },
    });
  }
  tooltip(name: string): string {
    switch (name) {
      case 'cloud_upload': return $localize`:@@einvoice.buttons.tooltip.cloud_upload:Open e-invoice`;
      case 'print': return $localize`:@@einvoice.buttons.tooltip.print:Print e-invoice`;
      case 'info': return $localize`:@@einvoice.buttons.tooltip.info:Show additional information`;
      case 'check': return $localize`:@@einvoice.buttons.tooltip.check:Validate e-invoice`;
      case 'zoom_in': return $localize`:@@einvoice.buttons.tooltip.zoom_in:Zoom in`;
      case 'pageview': return $localize`:@@einvoice.buttons.tooltip.pageview:Reset zoom to 100%`;
      case 'zoom_out': return $localize`:@@einvoice.buttons.tooltip.zoom_out:Zoom out`;
      default: return '';
    }
  }

  async onFileChange(pFileList: File[]) {
    this.dragOver = false;
    this.einvoiceViewerService.filesAdded(pFileList);
  }
  setDragOver() {
    if (!this.dragAndDropEnabled) {
      return;
    }
    this.dragOver = true;
  }
  setDragLeave() {
    if (!this.dragAndDropEnabled) {
      return;
    }
    this.dragOver = false;
  }

  get dragAndDropEnabled(): boolean {
    return this.configuration?.dragAndDropEnabled ?? true;
  }
  get validationMode(): EInvoiceValidationMode {
    return this.configuration?.validation?.mode ?? 'auto';
  }
  get showButtonBar(): boolean {
    return this.configuration?.buttonBar !== undefined &&
      Object.values(this.configuration.buttonBar).some(v => v === true);
  }
  get buttonsToolbarVisible(): boolean {
    return this.showButtonBar && this.showButtonsInToolbar;
  }
  get modelTreeViewEnabled(): boolean {
    return this.configuration?.views?.modelTree === true;
  }
  get pdfViewEnabled(): boolean {
    return this.configuration?.views?.pdf === true && !!this.pdfBytes?.byteLength;
  }
  get xmlViewEnabled(): boolean {
    return this.configuration?.views?.xml === true;
  }
  get viewSwitcherVisible(): boolean {
    return this.pdfViewEnabled || this.modelTreeViewEnabled || this.xmlViewEnabled;
  }
  get responsiveViewSwitcherVisible(): boolean {
    return this.viewSwitcherVisible && this.showViewSwitcherInToolbar;
  }
  get toolbarSearchVisible(): boolean {
    return this.einvoiceViewerSearchService.searchVisible();
  }
  get responsiveToolbarSearchVisible(): boolean {
    return this.toolbarSearchVisible && this.showSearchInToolbar;
  }
  get toolbarSearchQuery(): string {
    return this.einvoiceViewerSearchService.query();
  }
  get toolbarSearchHasQuery(): boolean {
    return this.toolbarSearchQuery.trim().length > 0;
  }
  get toolbarSearchExactMatch(): boolean {
    return this.einvoiceViewerSearchService.exactMatch();
  }
  get toolbarSearchExactMatchTooltip(): string {
    return this.toolbarSearchExactMatch
      ? $localize`:@@einvoice.viewer.search.exact.disable:Disable exact match`
      : $localize`:@@einvoice.viewer.search.exact.enable:Enable exact match`;
  }
  get toolbarSearchExactMatchLabel(): string {
    return $localize`:@@einvoice.viewer.search.exact.label:Exact match`;
  }
  get toolbarSearchPosition(): string {
    return this.einvoiceViewerSearchService.searchPosition();
  }
  get toolbarSearchCanNavigate(): boolean {
    return this.einvoiceViewerSearchService.canNavigate();
  }
  get validationStatusVisible(): boolean {
    return this.validationState.status !== 'idle';
  }
  get responsiveValidationStatusVisible(): boolean {
    return this.validationStatusVisible && this.showValidationInToolbar;
  }
  get searchInOverflow(): boolean {
    return this.toolbarSearchVisible && !this.showSearchInToolbar;
  }
  get viewSwitcherInOverflow(): boolean {
    return this.viewSwitcherVisible && !this.showViewSwitcherInToolbar;
  }
  get validationInOverflow(): boolean {
    return this.validationStatusVisible && !this.showValidationInToolbar;
  }
  get buttonsInOverflow(): boolean {
    return this.showButtonBar && !this.showButtonsInToolbar;
  }
  get overflowActive(): boolean {
    return this.searchInOverflow || this.viewSwitcherInOverflow || this.validationInOverflow || this.buttonsInOverflow;
  }
  get overflowTooltip(): string {
    return $localize`:@@einvoice.viewer.toolbar.more:More options`;
  }
  get formatLabel(): string {
    return this.format?.name ?? '';
  }
  get formatLogoSrc(): string | null {
    const id = this.format?.id;
    if (id && EinvoiceViewerComponent.FORMAT_LOGOS.has(id)) {
      return `assets/icons/${id}.png`;
    }
    return null;
  }
  get validationBadgeTooltip(): string {
    if (!this.format) {
      return '';
    }

    return `${this.format.name} ${this.format.version} ${this.format.syntax}`;
  }
  get validationReportAvailable(): boolean {
    const state = this.validationState;
    return state.status === 'invalid' || (state.status === 'valid' && !!state.warningCount);
  }
  onValidationBadgeClick(): void {
    if (this.validationReportAvailable) {
      this.showValidationDetails();
    }
  }
  get validationButtonVisible(): boolean {
    return this.validationMode === 'manual' && this.configuration?.buttonBar?.validate === true;
  }
  triggerValidation(): void {
    this.einvoiceViewerService.requestValidation();
    this.validationRequested.emit();
  }
  setToolbarSearchQuery(query: string): void {
    this.einvoiceViewerSearchService.setSearchQuery(query);
    this.scheduleToolbarLayout();
  }
  submitToolbarSearch(): void {
    this.einvoiceViewerSearchService.submitSearch();
    this.scheduleToolbarLayout();
  }
  toggleToolbarSearchExactMatch(): void {
    this.einvoiceViewerSearchService.setExactMatch(!this.toolbarSearchExactMatch);
    this.scheduleToolbarLayout();
  }
  previousToolbarSearchResult(): void {
    this.einvoiceViewerSearchService.previousSearchResult();
  }
  nextToolbarSearchResult(): void {
    this.einvoiceViewerSearchService.nextSearchResult();
  }
  showValidationDetails(): void {
    this.einvoiceViewerService.requestValidationDetails();
  }
  registerSvgIcons() {
    this.matIconRegistry.addSvgIcon('cloud_upload', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/cloud_upload.svg'));
    this.matIconRegistry.addSvgIcon('pageview', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/pageview.svg'));
    this.matIconRegistry.addSvgIcon('zoom_in', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/zoom_in.svg'));
    this.matIconRegistry.addSvgIcon('zoom_out', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/zoom_out.svg'));
    this.matIconRegistry.addSvgIcon('print', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/print.svg'));
    this.matIconRegistry.addSvgIcon('info', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/info.svg'));
    this.matIconRegistry.addSvgIcon('error', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/error.svg'));
    this.matIconRegistry.addSvgIcon('warning', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/warning.svg'));
    this.matIconRegistry.addSvgIcon('check', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/check.svg'));
    this.matIconRegistry.addSvgIcon('more_vert', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/more_vert.svg'));
  }
  error(error: { message: string, title?: string }) {
    this.einvoiceViewerService.error(error);
    this.onError.emit(error);
  }

  private updateContextFromInputs(): void {
    this.einvoiceViewerContextService.setDocumentContext({ model: this.document, format: this.format, rawXml: this.rawXml, xmlFileName: this.xmlFileName });
    this.applyExternalSelection();
  }

  private applyExternalSelection(): void {
    if (this.selectedModelPaths !== undefined) {
      this.einvoiceViewerContextService.setSelectedModelPaths(this.selectedModelPaths, this.focusedModelPath);
      return;
    }

    if (this.focusedModelPath !== undefined) {
      this.einvoiceViewerContextService.focusModelPath(this.focusedModelPath);
    }
  }

  private ensureActiveViewAvailable(): void {
    if (!this.isViewEnabled(this.activeView)) {
      this.activeView = 'document';
    }

    this.einvoiceViewerSearchService.setActiveView(this.activeView);
  }

  private isViewEnabled(view: EInvoiceViewerView): boolean {
    switch (view) {
      case 'document':
        return true;
      case 'pdf':
        return this.pdfViewEnabled;
      case 'modelTree':
        return this.modelTreeViewEnabled;
      case 'xml':
        return this.xmlViewEnabled;
      default:
        return false;
    }
  }

  private scheduleToolbarLayout(): void {
    if (typeof requestAnimationFrame === 'undefined') {
      this.updateToolbarVisibility();
      return;
    }

    if (this.toolbarLayoutFrame !== undefined) {
      cancelAnimationFrame(this.toolbarLayoutFrame);
    }

    this.toolbarLayoutFrame = requestAnimationFrame(() => {
      this.toolbarLayoutFrame = undefined;
      this.updateToolbarVisibility();
    });
  }

  private updateToolbarVisibility(): void {
    const toolbar = this.viewerToolbar?.nativeElement;
    if (!toolbar) {
      return;
    }

    // Reset every section to its natural (configuration-driven) visibility so the
    // toolbar can expand again when horizontal space becomes available.
    this.showButtonsInToolbar = this.showButtonBar;
    this.showValidationInToolbar = this.validationStatusVisible;
    this.showSearchInToolbar = this.toolbarSearchVisible;
    this.showViewSwitcherInToolbar = this.viewSwitcherVisible;
    this.cdr.detectChanges();

    // Collapse sections one at a time, in priority order, until the toolbar fits
    // on a single row: search → view selector → validation → buttons (all).
    const collapseOrder = [
      { visible: () => this.showSearchInToolbar, hide: () => (this.showSearchInToolbar = false) },
      { visible: () => this.showViewSwitcherInToolbar, hide: () => (this.showViewSwitcherInToolbar = false) },
      { visible: () => this.showValidationInToolbar, hide: () => (this.showValidationInToolbar = false) },
      { visible: () => this.showButtonsInToolbar, hide: () => (this.showButtonsInToolbar = false) }
    ];

    for (const section of collapseOrder) {
      if (!this.isToolbarOverflowing(toolbar)) {
        break;
      }
      if (section.visible()) {
        section.hide();
        this.cdr.detectChanges();
      }
    }

    this.cdr.markForCheck();
  }

  private isToolbarOverflowing(toolbar: HTMLElement): boolean {
    return toolbar.scrollWidth > toolbar.clientWidth + 1;
  }
}
